import { MedChronVersionStatus } from "@/constants";
import { z } from "zod";

export const MedVisitCitationSchema = z
  .object({
    file_id: z.string(),
    start_page: z.number().int().min(1),
    end_page: z.number().int().min(1),
  })
  .refine((citation) => citation.start_page <= citation.end_page, {
    message: "start_page must not be after end_page",
  });

export type MedVisitCitation = z.infer<typeof MedVisitCitationSchema>;

export interface MedVisitCreateDetail {
  visit_detail_category: string;
  visit_details: string;
}

export interface MedVisitCreateRequest {
  visit_date: string | null;
  visit_type_id: string;
  provider_name: string | null;
  facility_name: string | null;
  visit_subtype?: string | null;
  citations?: MedVisitCitation[];
  details?: MedVisitCreateDetail[];
}

/** Full PUT body; omitting citations leaves the citation list alone. */
export interface MedVisitUpdateRequest {
  visit_date: string | null;
  visit_type_id: string;
  provider_name: string | null;
  facility_name: string | null;
  visit_subtype: string | null;
  citations?: MedVisitCitation[];
}

/**
 * Names the chronology version that holds an edit.
 * - forked is true when the edit landed on a new version forked from a finished case.
 */
export const MedChronEditResultSchema = z.object({
  version_id: z.string(),
  forked: z.boolean(),
});

export type MedChronEditResult = z.infer<typeof MedChronEditResultSchema>;

const MedVisitDetailResponseSchema = z.object({
  visit_detail_category: z.string(),
  visit_details: z.string(),
  order_id: z.number().int(),
});

export const MedVisitDetailEditResponseSchema =
  MedVisitDetailResponseSchema.merge(MedChronEditResultSchema);

export type MedVisitDetailEditResponse = z.infer<typeof MedVisitDetailEditResponseSchema>;

export const MedVisitEditResponseSchema = MedChronEditResultSchema.extend({
  visit_id: z.string(),
  visit_date: z.string().nullish(),
  visit_type_id: z.string(),
  provider_name: z.string().nullish(),
  facility_name: z.string().nullish(),
  visit_subtype: z.string().nullish(),
  citations: MedVisitCitationSchema.array(),
  med_visit_details: MedVisitDetailResponseSchema.array(),
  source_version_id: z.string().nullish(),
});

export type MedVisitEditResponse = z.infer<typeof MedVisitEditResponseSchema>;

export const MedVisitCreateResponseSchema = MedChronEditResultSchema.extend({
  visit_id: z.string(),
});

export type MedVisitCreateResponse = z.infer<typeof MedVisitCreateResponseSchema>;

export const MedVisitDeleteResponseSchema = MedChronEditResultSchema.extend({
  visit_id: z.string(),
});

export type MedVisitDeleteResponse = z.infer<typeof MedVisitDeleteResponseSchema>;

export const MedVisitDetailDeleteResponseSchema = MedChronEditResultSchema.extend({
  visit_id: z.string(),
  visit_detail_category: z.string(),
});

export type MedVisitDetailDeleteResponse = z.infer<typeof MedVisitDetailDeleteResponseSchema>;

/** PATCH body for a version; the API rejects an empty body and is_current: false. */
export type MedChronVersionUpdateRequest =
  | { version_name: string; is_current?: true }
  | { version_name?: string; is_current: true };

export const MedChronVersionSchema = z.object({
  version_id: z.string(),
  version_name: z.string().nullable(),
  version_number: z.number().int(),
  created_at: z.string().nullable(),
  is_current: z.boolean(),
  tags: z.string().array(),
  file_ids: z.string().array().nullable(),
  status: z.nativeEnum(MedChronVersionStatus),
});

export type MedChronVersion = z.infer<typeof MedChronVersionSchema>;

export const MedChronVersionDuplicateResponseSchema = z.object({
  version_id: z.string(),
});

export type MedChronVersionDuplicateResponse = z.infer<
  typeof MedChronVersionDuplicateResponseSchema
>;

export const MedVisitBulkUpdateResponseSchema = MedChronEditResultSchema.extend({
  updated_count: z.number().int(),
});

export type MedVisitBulkUpdateResponse = z.infer<typeof MedVisitBulkUpdateResponseSchema>;

export const MedVisitBulkDeleteResponseSchema = MedChronEditResultSchema.extend({
  deleted_count: z.number().int(),
  deleted_visit_ids: z.string().array(),
});

export type MedVisitBulkDeleteResponse = z.infer<typeof MedVisitBulkDeleteResponseSchema>;

export const MedVisitBulkCopyResponseSchema = MedChronEditResultSchema.extend({
  copied_visits: z.object({ visit_id: z.string() }).array(),
});

export type MedVisitBulkCopyResponse = z.infer<typeof MedVisitBulkCopyResponseSchema>;

export const MedPatientBackgroundEditResponseSchema = MedChronEditResultSchema.extend({
  prior_medical_history: z.string().nullish(),
  pre_existing_conditions: z.string().nullish(),
  family_history: z.string().nullish(),
  social_history: z.string().nullish(),
  allergies_medications: z.string().nullish(),
});

export type MedPatientBackgroundEditResponse = z.infer<
  typeof MedPatientBackgroundEditResponseSchema
>;
